import React from 'react';
import axios from 'axios';

import PermissionService from 'pa-react/dist/util/PermissionService';

import APIConfig from 'config/api';
import { CompanyType } from 'constants/company';
import { DOMAIN_OBJECT_DESCRIPTION, PRODUCT, SCREEN_DESCRIPTION } from 'constants/product';
import { PersonType } from 'util/person';
import { useQueryParameters } from 'hooks/url/useQueryParameters';

//
// --- Types ---

interface LoggedInPersonContextValue {
  /** an axios error if request fails */
  error?: string;
  /** true if person or person company request is running */
  isLoading: boolean;
  /** true if person only has view permissions on the page */
  isViewOnly: boolean;
  /** full person data object */
  person: PersonType | null;
  /** full company data object for person*/
  personCompany: CompanyType | null;
}

// reducer state

interface LoggedInPersonContextState
  extends Pick<LoggedInPersonContextValue, 'error' | 'isLoading' | 'isViewOnly' | 'person' | 'personCompany'> {}

// reducer actions

interface PersonLoadErrorAction {
  type: typeof PERSON_LOAD_ERROR;
  error: string;
}

interface PersonLoadSuccessAction {
  type: typeof PERSON_LOAD_SUCCESS;
  person: PersonType;
  personCompany: CompanyType;
}

interface SetPermissionsAction {
  type: typeof SET_PERMISSIONS;
  isViewOnly: boolean;
}

interface SetPersonLoadingAction {
  type: typeof SET_PERSON_LOADING;
  isLoading: boolean;
}

type LoggedInPersonContextAction =
  | PersonLoadErrorAction
  | PersonLoadSuccessAction
  | SetPermissionsAction
  | SetPersonLoadingAction;

//
// --- Reducer ---

/**
 * Context for storing logged in user data
 */
const LoggedInPersonContext = React.createContext<LoggedInPersonContextValue>({} as LoggedInPersonContextValue);

/**
 * useLoggedInPerson hook
 *
 * external hook that returns person and company object for logged in person, or null if not logged in or not valid
 */
export const useLoggedInPerson = (): LoggedInPersonContextValue => {
  const context = React.useContext(LoggedInPersonContext);

  if (!context.hasOwnProperty('person')) {
    throw new Error('useLoggedInPerson must be used within a LoggedInPersonProvider');
  }

  return context;
};

//
// --- LoggedInPersonProvider Default State ---

const defaultState: LoggedInPersonContextState = {
  error: undefined,
  isLoading: false,
  isViewOnly: false,
  person: null,
  personCompany: null
};

//
// --- LoggedInPersonProvider Reducer ---

const PERSON_LOAD_ERROR = 'person-load-error';
const PERSON_LOAD_SUCCESS = 'person-loaded';
const SET_PERMISSIONS = 'set-permissions';
const SET_PERSON_LOADING = 'set-person-loading';

//

function loggedInPersonReducer(
  state: LoggedInPersonContextState,
  action: LoggedInPersonContextAction
): LoggedInPersonContextState {
  switch (action.type) {
    case PERSON_LOAD_ERROR:
      return {
        ...state,
        error: action.error,
        isLoading: false,
        person: defaultState.person,
        personCompany: defaultState.personCompany
      };

    case PERSON_LOAD_SUCCESS:
      return {
        ...state,
        error: defaultState.error,
        isLoading: false,
        person: action.person,
        personCompany: action.personCompany
      };

    case SET_PERSON_LOADING:
      return { ...state, isLoading: action.isLoading };

    case SET_PERMISSIONS:
      return { ...state, isViewOnly: action.isViewOnly };

    default:
      return state;
  }
}

function useLoggedInPersonProvider(): LoggedInPersonContextValue {
  const [state, dispatch] = React.useReducer(loggedInPersonReducer, defaultState);
  const { offeringKey, portalId } = useQueryParameters();

  React.useEffect(() => {
    async function requestLoggedInPerson() {
      try {
        dispatch({ type: SET_PERSON_LOADING, isLoading: true });

        // init will redirect user to login (and back), if no valid session
        await PermissionService.init();

        // person response cached inside PermissionService, so this will not generate a duplicate API call
        const [person, personCompanyResponse] = await Promise.all([
          PermissionService.getLoggedInPerson(),
          axios.request(APIConfig.Permission.getLoggedInPersonCompany())
        ]);

        if (portalId) {
          const [viewResponse, domainObjectView] = await Promise.all([
            axios.request(
              APIConfig.Permission.hasScreenPermissionForPortal(
                PRODUCT.SUPPLIER_INTELLIGENCE,
                SCREEN_DESCRIPTION.SETUP_SUPPLIER_PORTAL,
                portalId
              )
            ),
            PermissionService.canView(PRODUCT.SUPPLIER_INTELLIGENCE, DOMAIN_OBJECT_DESCRIPTION.PORTAL, portalId)
          ]);
          const hasViewPermissions = viewResponse.data && domainObjectView;
          if (!hasViewPermissions) {
            throw new Error('No view permissions on this portal.');
          }
          let isViewOnly;
          const [domainObjectEdit, isViewOnlyResponse] = await Promise.all([
            PermissionService.canEdit(PRODUCT.SUPPLIER_INTELLIGENCE, DOMAIN_OBJECT_DESCRIPTION.PORTAL, portalId),
            axios.request(APIConfig.Portal.getIsViewOnlyForPortal(portalId))
          ]);
          isViewOnly = !domainObjectEdit || isViewOnlyResponse.data;
          dispatch({ type: SET_PERMISSIONS, isViewOnly });
        } else if (offeringKey) {
          const [viewResponse, domainObjectView] = await Promise.all([
            axios.request(
              APIConfig.Permission.hasScreenPermissionForEvent(
                PRODUCT.SOURCING_INTELLIGENCE,
                SCREEN_DESCRIPTION.SETUP_SUPPLIER_BID_EVENT,
                offeringKey
              )
            ),
            PermissionService.canView(PRODUCT.SOURCING_INTELLIGENCE, DOMAIN_OBJECT_DESCRIPTION.BID_EVENT, offeringKey)
          ]);
          const hasViewPermissions = viewResponse.data && domainObjectView;
          if (!hasViewPermissions) {
            throw new Error('No view permissions on this bid event.');
          }
          let isViewOnly;
          const isViewOnlyResponse = await axios.request(APIConfig.Sourcing.getIsViewOnlyForBidEvent(offeringKey));
          isViewOnly = isViewOnlyResponse.data;
          dispatch({ type: SET_PERMISSIONS, isViewOnly });
        }

        const personCompany = personCompanyResponse.data ? personCompanyResponse.data : null;

        if (!person || person.personkey === undefined) {
          dispatch({ type: PERSON_LOAD_ERROR, error: 'Could not determine current user' });
        } else if (!personCompany) {
          dispatch({ type: PERSON_LOAD_ERROR, error: 'Could not determine current user company' });
        } else {
          dispatch({ type: PERSON_LOAD_SUCCESS, person, personCompany });
        }
      } catch (error) {
        dispatch({
          type: PERSON_LOAD_ERROR,
          error: `App error: ${axios.isAxiosError(error) || error instanceof Error ? error.message : `${error}`}`
        });
      }
    }

    requestLoggedInPerson();
  }, [offeringKey, portalId]);

  return {
    error: state.error,
    isViewOnly: state.isViewOnly,
    isLoading: state.isLoading,
    person: state.person,
    personCompany: state.personCompany
  };
}

/**
 * Logged in user context provider component
 */
export const LoggedInPersonProvider: React.FC = props => {
  const { children } = props;

  const person = useLoggedInPersonProvider();

  return <LoggedInPersonContext.Provider value={person}>{children}</LoggedInPersonContext.Provider>;
};
